import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { IPasswordStrengthData } from '../../models/login';
import { validatePasswordStrength } from '../../services/login/loginApi-service';
import { encryptPassword, formatMessageWithBoldTitle, getRSAPasswordPublicKey } from '../../utils/helper';

const removeInvalidCharacters = (text: string) => text.replace(/[^\p{L}\d!@#$%^&*()\-_=+{};:,<.>]+/gu, '');

const ReactstrapFormikFloatingLabelPasswordInput = ({
  field,
  form: { touched, errors, setFieldTouched, setFieldValue, setFieldError, validateField },
  label,
  id,
  className,
  inputClassName,
  isShowStrengthBar = false,
  passwordStrengthRef,
  ...props
}) => {
  const { t } = useTranslation();
  const defaultWeakStrength = useMemo<IPasswordStrengthData>(
    () => ({
      colorCode: '#dc3545',
      defaultColor: '#dee2e6',
      barState: t('login.label.weak'),
      message: null,
      coverage: 25,
      score: 0,
    }),
    [t],
  );
  const [isFocused, setFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [strength, setStrength] = useState<IPasswordStrengthData>(defaultWeakStrength);
  const [isChecking, setIsChecking] = useState(false);

  const internalStrengthRef = useRef('');
  const activeStrengthRef = passwordStrengthRef ?? internalStrengthRef;
  const rsaKeyRef = useRef('');

  const fetchStrength = async (key: string, value: string): Promise<IPasswordStrengthData> => {
    try {
      const encryptedPassword = await encryptPassword(value, key);
      const response = await validatePasswordStrength(encryptedPassword);
      return response.Data;
    } catch {
      return defaultWeakStrength;
    }
  };

  const applyStrengthResult = (result: IPasswordStrengthData) => {
    activeStrengthRef.current = result.message ?? '';
    validateField(field.name);
    if (result.message) setFieldError(field.name, result.message);
  };

  const checkAndSetStrength = async (key: string, value: string) => {
    setIsChecking(true);
    try {
      const result = await fetchStrength(key, value);
      setStrength(result);
      applyStrengthResult(result);
    } catch {
      setStrength(defaultWeakStrength);
    } finally {
      setIsChecking(false);
    }
  };

  const debouncedCheckStrength = useMemo(
    () =>
      debounce(async (value: string) => {
        await checkAndSetStrength(rsaKeyRef.current, value);
      }, 500),
    [],
  );

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;
    (async () => {
      rsaKeyRef.current = await getRSAPasswordPublicKey();
    })();
  }, []);

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;

    setStrength((prev) => {
      if (prev.score === 0) return defaultWeakStrength;
      return prev;
    });
  }, [defaultWeakStrength]);

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;

    if (!field.value) {
      activeStrengthRef.current = '';
      setStrength(defaultWeakStrength);
      setIsChecking(false);
      debouncedCheckStrength.cancel();
      return undefined;
    }

    debouncedCheckStrength(field.value);
    return () => debouncedCheckStrength.cancel();
  }, [field.value]);

  const handleFocus = () => setFocus(true);

  const handleBlur = () => {
    setFocus(false);
    setFieldTouched(field.name, true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(field.name, removeInvalidCharacters(event.target.value));
  };

  const onShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const floatLabel = isFocused || field.value;

  const barColor = strength?.colorCode || strength?.defaultColor;
  const showStrengthBar = Boolean(field.value && isShowStrengthBar);
  const visibleError = errors[field.name] && errors[field.name] !== 'required' ? errors[field.name] : null;

  return (
    <Form.Group className={`${className || ''} outlined-floating-input`}>
      <div className={`input-wrapper password-block ${showPassword ? 'show' : ''} floating-label-password-wrapper`}>
        <Form.Control
          {...field}
          {...props}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleInputChange}
          isInvalid={Boolean(touched[field.name] && errors[field.name])}
          className={`${inputClassName || ''} floating-label-password`}
          type={showPassword ? 'text' : 'password'}
          maxLength={64}
        />
        {label && (
          <label className={`floating-label ${floatLabel ? 'floating' : ''}`} htmlFor={id}>
            {label}
          </label>
        )}
        <span
          className="password-icon floating-password-icon floating-label-password-icon"
          onClick={onShowPassword}
        ></span>
      </div>

      {isFocused && isShowStrengthBar && (
        <>
          <p className="password-note p-0 mt-2">{formatMessageWithBoldTitle(t('login.label.passwordNote'))}</p>
        </>
      )}

      {strength && showStrengthBar && (
        <div className="strength-bar-wrapper">
          <span className="strength-bar-label" style={{ color: isChecking ? '#36454F' : barColor }}>
            {strength.barState}
          </span>

          <div className="strength-bar-track" style={{ background: strength.defaultColor }}>
            {isChecking ? (
              <div className="strength-bar-shimmer" />
            ) : (
              <div
                className="strength-bar-fill"
                style={{
                  width: `${strength.coverage}%`,
                  backgroundColor: barColor,
                }}
              />
            )}
          </div>
        </div>
      )}

      {visibleError && (
        <Form.Control.Feedback type="invalid" className="pb-0 invalid-feedback d-block invalid-password-feedback">
          {visibleError}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default ReactstrapFormikFloatingLabelPasswordInput;
